import { Card } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent, useContext } from "react";
import { v1 as uuidv1 } from "uuid";
import { PacePlan } from "../../@types";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";
import { AppStore } from "../../app/store";
import { CollapsibleBox } from "../../components";
import { formatTime, makePlanDisplayId, useTranslate } from "../../helpers";
import { addProtocolToUrl } from "../../helpers/objectUtilities";
import { getPlanName } from "../../helpers/planHelpers";

interface PacePlanCardProps {
  pacePlan: PacePlan;
}

const PacePlanCard: FunctionComponent<PacePlanCardProps> = ({ pacePlan }) => {
  const t = useTranslate();
  const {
    state: { language },
    dispatch,
  } = useContext(AppStore.AppContext);

  const planId = makePlanDisplayId(pacePlan);
  const visitSiteLinkId = uuidv1();

  const spanPhoneLinkId = uuidv1();
  const phoneLinkId = uuidv1();

  const spanTtyLinkId = uuidv1();
  const ttyLinkId = uuidv1();

  return (
    <Card className="PacePlanCard ds-u-padding--2 ds-u-margin-top--3">
      <div className="ds-l-row">
        <div className="PacePlanCard__cardColumn">
          <h2 className="ds-u-margin-bottom--1" id={planId}>
            {getPlanName(pacePlan, language)}
          </h2>
          <div className="ds-u-color--gray-light">
            {t("pace.plans.plan_id")} {planId}
          </div>
          <p>
            {pacePlan.prospect_phones.local_phone && (
              <>
                <span
                  id={spanPhoneLinkId}
                  className="ds-u-font-weight--semibold"
                >
                  {t("pace.plans.phone_number")}
                </span>
                :{" "}
                <a
                  id={phoneLinkId}
                  aria-labelledby={`${planId} ${spanPhoneLinkId} ${phoneLinkId}`}
                  href={`tel:${pacePlan.prospect_phones.local_phone.number}`}
                  onClick={() => {
                    dispatch({
                      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                      settings: {
                        event_action: "Phone Click",
                        event_label: `${pacePlan.state}:${pacePlan.plan_name}:${planId}:${pacePlan.prospect_phones.local_phone?.number}`,
                        other_props: { event_category: "PACE" },
                      },
                    });
                  }}
                >
                  {pacePlan.prospect_phones.local_phone.number}
                </a>
                {pacePlan.prospect_phones.local_phone.extension && (
                  <span>
                    {" "}
                    x{pacePlan.prospect_phones.local_phone.extension}
                  </span>
                )}
              </>
            )}
            {pacePlan.prospect_phones.local_tty && (
              <>
                <br />
                <span id={spanTtyLinkId} className="ds-u-font-weight--semibold">
                  {t("pace.plans.tty")}
                </span>
                :{" "}
                <a
                  id={ttyLinkId}
                  aria-labelledby={`${planId} ${spanTtyLinkId} ${ttyLinkId}`}
                  href={`tel:${pacePlan.prospect_phones.local_tty.number}`}
                  onClick={() => {
                    dispatch({
                      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                      settings: {
                        event_action: "Phone Click",
                        event_label: `${pacePlan.state}:${pacePlan.plan_name}:${planId}:${pacePlan.prospect_phones.local_tty?.number}`,
                        other_props: { event_category: "PACE" },
                      },
                    });
                  }}
                >
                  {pacePlan.prospect_phones.local_tty.number}
                </a>
                {pacePlan.prospect_phones.local_tty.extension && (
                  <span> x{pacePlan.prospect_phones.local_tty.extension}</span>
                )}
              </>
            )}
          </p>
          <p>
            <a
              id={visitSiteLinkId}
              href={addProtocolToUrl(pacePlan.website)}
              aria-labelledby={`${planId} ${visitSiteLinkId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mct-hide-print ds-c-button"
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action: "Visit Plan Website",
                    event_label: `${pacePlan.state}:${pacePlan.plan_name}:${planId}`,
                    other_props: { event_category: "PACE" },
                  },
                });
              }}
            >
              {t("pace.plans.visit_plan_website")}
            </a>
            <span className="mct-print-only">{pacePlan.website}</span>
          </p>
        </div>
        <div className="ds-l-col--1 ds-u-border-right--1"></div>
        <div className="ds-l-col--1"></div>
        <div className="PacePlanCard__cardColumn">
          <CollapsibleBox
            containerClassname="PacePlanCard__mobileOfficeHoursBox"
            titleClassname="PacePlanCard__mobileOfficeHoursBox_trigger"
            label={
              <h3 className="PacePlanCard__sectionHeading ds-u-display--inline-block">
                {t("pace.plans.office_hours")}
              </h3>
            }
          >
            <h3 className="PacePlanCard__sectionHeading PacePlanCard__officeHoursHeading">
              {t("pace.plans.office_hours")}
            </h3>
            <ol className="ds-c-list--bare" role="list">
              {pacePlan.office_hours?.map(day => {
                let time = "";
                if (!day.open) {
                  time = t("closed");
                } else if (day.open_all_day) {
                  time = t("availaible_24_hours");
                } else {
                  time = `${formatTime(day.open_time)} - ${formatTime(
                    day.close_time
                  )}`;
                }
                return (
                  <li key={`${planId}_${day.day}`} role="listitem">
                    {t(day.day.toLowerCase())}: {time}
                  </li>
                );
              })}
            </ol>
          </CollapsibleBox>
        </div>
      </div>
    </Card>
  );
};

export default PacePlanCard;
